import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { categorySchema } from "@/utils/schemaHelper";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";
import { updateCategoryProps } from "@/types/organization";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";

import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import {
  getCategoryApi,
  updateCategoryApi,
} from "@/modules/organizationServices";
import {
  setCategories,
  updateCategory,
} from "@/store/redux/organization/categorySlice";

import { OrganizationType } from "@/types/apiResposne";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { CategoryMessage } from "@/enums/errorMessages";
import { ReusableButton } from "@/reusable/reusableButton";

const UpdateCategory: React.FC<updateCategoryProps> = ({
  onClose,
  isOpen,
  category,
}) => {
  if (!isOpen) return null;

  const dispatch = useDispatch();

  const loading = useSelector((state: RootState) => state.app.loading);
  const form = useForm<z.infer<typeof categorySchema>>({
    resolver: zodResolver(categorySchema),
    mode: "onChange",
    defaultValues: {
      name: category.name,
      organization: category.organization,
    },
  });

  const getCategories = async () => {
    try {
      const res = await getCategoryApi();
      const response = res.categories;
      if (response != undefined) {
        const categories: OrganizationType.CategoryResponse[] = Array.isArray(
          response,
        )
          ? response
          : [response];
        dispatch(setCategories(categories));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const onSubmit = async (values: z.infer<typeof categorySchema>) => {
    if (loading) return;
    dispatch(setLoading(true));

    try {
      await updateCategoryApi(values, category.id);

      dispatch(updateCategory({ id: category.id, ...values }));
      getCategories();
      onClose();

      toastMessage("success", CategoryMessage.CATEGORY_UPDATED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 sm:space-y-8 mt-5"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="max-h-13">
                  <FormLabel>Name</FormLabel>

                  <FormControl>
                    <Input className="bg-white" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organization"
              render={({ field }) => (
                <FormItem className="max-h-13">
                  <FormLabel hidden>Organization</FormLabel>
                  <FormControl>
                    <Input hidden {...field} value={category.organization} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-center mt-4 mr-2">
              <ReusableButton
                label="Update"
                className=" mt-5 mb-2 "
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateCategory;
